import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSSEConnected, useSSEEvent } from '../hooks/useSSE'

// ═══ Types ═══════════════════════════════════════════════════════

interface ChatterEntry {
  id: string
  sender_name: string
  chatter: string
  created_at: string
  conversation_id: string
  message_id?: string
}

interface Props {
  conversationId: string
  onScrollToMessage?: (messageId: string) => void
}

// ═══ Persona Colors (shared with ConversationViewPage) ═════════

const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', gretchen: '#B87DB8', vigil: '#708090', herald: '#C0785A',
  grain: '#A5886F', loom: '#7B6FA0', noma: '#E8A87C', counsel: '#6B7B8D',
  pulse: '#60A0B0', cadence: '#9B7A6C', anvil: '#7D7D7D', rivet: '#8C9440',
  system: '#484f58',
}

function senderColor(name: string): string {
  return PERSONA_COLORS[name?.toLowerCase()] || '#8b949e'
}

function displayName(name: string): string {
  if (!name) return '?'
  return name[0].toUpperCase() + name.slice(1)
}

// ═══ Grouped entries — consecutive same-sender ══════════════════

interface GroupedEntry extends ChatterEntry {
  showHeader: boolean
}

function groupEntries(entries: ChatterEntry[]): GroupedEntry[] {
  const result: GroupedEntry[] = []
  let lastSender = ''

  for (const entry of entries) {
    const sameSender = entry.sender_name.toLowerCase() === lastSender.toLowerCase()
    result.push({
      ...entry,
      showHeader: !sameSender,
    })
    lastSender = entry.sender_name
  }

  return result
}

// ═══ ChatterBox Component ═══════════════════════════════════════

export default function ChatterBox({ conversationId, onScrollToMessage }: Props) {
  const sseOk = useSSEConnected()
  const queryClient = useQueryClient()
  const scrollRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)

  // Resize state — persisted per conversation
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(`chatterbox-width-${conversationId}`)
    return saved ? parseInt(saved) : 360
  })

  // Minimize state — persisted per conversation
  const [minimized, _setMinimized] = useState(() => {
    const saved = localStorage.getItem(`cb-min-${conversationId}`)
    return saved === '1'
  })
  const setMinimized = (val: boolean) => {
    _setMinimized(val)
    localStorage.setItem(`cb-min-${conversationId}`, val ? '1' : '0')
  }
  useEffect(() => {
    const saved = localStorage.getItem(`cb-min-${conversationId}`)
    _setMinimized(saved === '1')
  }, [conversationId])

  const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  const { data } = useQuery({
    queryKey: ['chatterBox', conversationId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${conversationId}/chatter`, { headers })
      if (!res.ok) return { entries: [] }
      return res.json() as Promise<{ entries: ChatterEntry[] }>
    },
    enabled: !!conversationId,
    refetchInterval: sseOk ? false : 15_000,
  })

  // Listen for chatter_message SSE events
  useSSEEvent('chatter_message', useCallback((eventData: unknown) => {
    const d = eventData as { conversation_id?: string }
    if (d.conversation_id === conversationId) {
      queryClient.invalidateQueries({ queryKey: ['chatterBox', conversationId] })
    }
  }, [conversationId, queryClient]))

  const entries = data?.entries || []
  const grouped = groupEntries(entries)

  // "Since you last looked" delimiter — mark the boundary between entries
  // that existed when the page loaded and entries that arrived after.
  const mountTimeRef = useRef<string>(new Date().toISOString())
  const newEntryBoundaryId = (() => {
    for (let i = 0; i < entries.length; i++) {
      const ts = entries[i].created_at.includes('T') ? entries[i].created_at : entries[i].created_at.replace(' ', 'T') + 'Z'
      if (ts >= mountTimeRef.current) return entries[i].id
    }
    return null
  })()

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [entries.length, autoScroll])

  // Detect user scroll
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        setAutoScroll(false)
      } else {
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
        if (atBottom) setAutoScroll(true)
      }
    }
    el.addEventListener('wheel', onWheel, { passive: true })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  // Minimized state — thin vertical tab
  if (minimized) {
    return (
      <div
        onClick={() => setMinimized(false)}
        className="shrink-0 flex items-center justify-center cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
        style={{
          width: 24,
          borderLeft: '1px solid var(--border)',
          background: 'var(--bg-body)',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
        }}
        title="Expand Chatter Box"
      >
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)', letterSpacing: 2 }}>
          💬
        </span>
      </div>
    )
  }

  return (
    <>
      {/* Resize handle — left edge */}
      <div
        style={{ width: 5, cursor: 'col-resize', flexShrink: 0, background: 'transparent' }}
        onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--border)' }}
        onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent' }}
        onMouseDown={(e) => {
          e.preventDefault()
          const startX = e.clientX
          const startW = panelWidth
          const onMove = (ev: MouseEvent) => {
            // Dragging LEFT makes panel wider (startX - ev.clientX)
            const newW = Math.max(200, startW + (startX - ev.clientX))
            if (panelRef.current) panelRef.current.style.width = `${newW}px`
          }
          const onUp = (ev: MouseEvent) => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            const finalW = Math.max(200, startW + (startX - ev.clientX))
            setPanelWidth(finalW)
            localStorage.setItem(`chatterbox-width-${conversationId}`, String(finalW))
          }
          document.body.style.cursor = 'col-resize'
          document.body.style.userSelect = 'none'
          document.addEventListener('mousemove', onMove)
          document.addEventListener('mouseup', onUp)
        }}
      />
    <div
      ref={panelRef}
      className="flex flex-col h-full overflow-hidden"
      style={{
        width: panelWidth,
        minWidth: 200,
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--bg-body)',
      }}
    >
      {/* Header — chatter label + count + minimize + audio toggle */}
      <div
        className="shrink-0 px-4 py-3 flex items-center justify-between"
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: 'var(--text-secondary)', letterSpacing: 1.5 }}
          >
            Chatter
          </span>
          {entries.length > 0 && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                fontSize: 10,
                fontWeight: 500,
              }}
            >
              {entries.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Audio toggle — reserved for v2 audio layer */}
          <button
            title="Audio chatter coming soon — see the Chatter Guide"
            style={{
              background: 'transparent', border: 'none', cursor: 'default',
              fontSize: 14, opacity: 0.25, color: 'var(--text-muted)',
              padding: '2px 4px',
            }}
          >
            🔊
          </button>
          {/* Minimize button */}
          <button
            onClick={() => setMinimized(true)}
            title="Minimize Chatter Box"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 12, color: 'var(--text-muted)',
              padding: '2px 6px',
            }}
          >
            ▸
          </button>
        </div>
      </div>

      {/* Entries */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2"
      >
        {entries.length === 0 ? (
          <div
            className="flex items-center justify-center h-full text-center px-4"
            style={{ color: 'var(--text-muted)' }}
          >
            <div>
              <div className="text-xs" style={{ opacity: 0.6 }}>
                No chatter yet in this conversation.
              </div>
            </div>
          </div>
        ) : (
          grouped.map((entry, idx) => {
            // "New" delimiter — first entry that arrived after page load
            const showNewDelimiter = entry.id === newEntryBoundaryId && idx > 0
            const time = (() => {
              try {
                const d = entry.created_at.includes('Z') || entry.created_at.includes('+') || entry.created_at.includes('T')
                  ? new Date(entry.created_at)
                  : new Date(entry.created_at.replace(' ', 'T') + 'Z')
                return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
              } catch {
                return ''
              }
            })()

            const color = senderColor(entry.sender_name)
            const clickable = !!(entry.message_id && onScrollToMessage)

            return (
              <div key={entry.id}>
                {showNewDelimiter && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    margin: '10px 6px 6px',
                  }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--accent)', opacity: 0.35 }} />
                    <span style={{ fontSize: 9, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, whiteSpace: 'nowrap' }}>New</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--accent)', opacity: 0.35 }} />
                  </div>
                )}
              <div
                className="rounded transition-colors"
                style={{
                  paddingTop: entry.showHeader ? 8 : 1,
                  paddingBottom: 1,
                  paddingLeft: 6,
                  paddingRight: 6,
                  marginTop: entry.showHeader ? 4 : 0,
                  cursor: clickable ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (entry.message_id && onScrollToMessage) {
                    onScrollToMessage(entry.message_id)
                  }
                }}
                onMouseOver={e => {
                  if (clickable) e.currentTarget.style.background = 'var(--bg-elevated)'
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent'
                }}
              >
                {entry.showHeader && (
                  <div className="flex items-baseline gap-1.5 mb-0.5">
                    <span
                      className="text-xs font-semibold"
                      style={{ color }}
                    >
                      {displayName(entry.sender_name)}
                    </span>
                  </div>
                )}
                <div
                  className="flex items-start gap-1.5"
                  style={{ fontSize: 12, lineHeight: 1.4 }}
                >
                  <span
                    className="shrink-0"
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 10,
                      fontFamily: 'monospace',
                      paddingTop: 1,
                      minWidth: 36,
                    }}
                  >
                    {time}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {entry.chatter}
                  </span>
                </div>
              </div>
              </div>
            )
          })
        )}
      </div>
    </div>
    </>
  )
}

// ═══ Chatter Dot Indicator ══════════════════════════════════════
// Export a small component to render on messages that have chatter

export function ChatterDot({ senderName, chatterText }: { senderName: string; chatterText?: string }) {
  const color = senderColor(senderName)
  return (
    <span
      title={chatterText || 'Has chatter — see Chatter Box'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: `${color}22`,
        border: `1px solid ${color}55`,
        marginLeft: 4,
        verticalAlign: 'middle',
        cursor: 'help',
        fontSize: 8,
        lineHeight: 1,
      }}
    >
      <span style={{ color, fontWeight: 700 }}>💬</span>
    </span>
  )
}
